import { useState } from "react";
import { Check, Copy, Warning } from "@phosphor-icons/react";

import styles from "./credential-handover.module.css";

/**
 * CredentialHandover is the show-once panel for a secret the operator has to
 * carry out of band — a temporary password, or a recovery link.
 *
 * # THE RULES THIS COMPONENT EXISTS TO ENFORCE
 *
 * 1. The secret arrives as a prop, lives in this component's tree, and goes
 *    nowhere else. It is never written to localStorage, to a zustand store, to
 *    a react-query cache, to the URL, or to the console. There is no "show it
 *    again" affordance because there is nothing to show it from: admin-service
 *    does not persist it and cannot re-issue it.
 *
 * 2. It is visible on screen from the moment it renders. A masked field with a
 *    reveal toggle would be security theatre here — the operator's whole job at
 *    this moment is to read it and pass it on, and the threat this panel guards
 *    against is the secret being STORED, not the secret being briefly on a
 *    screen the operator is already looking at.
 *
 * 3. Copy-to-clipboard is offered because the alternative is retyping 24
 *    characters, and a mistyped password sends the operator back around a loop
 *    that cannot be repeated. `navigator.clipboard` is unavailable in
 *    non-secure contexts and in the jsdom used by tests, so the button degrades
 *    to a clear failure message rather than silently doing nothing — the text
 *    remains selectable either way.
 *
 * 4. Dismissing it is deliberate and one-way. The close control says so.
 */
export interface CredentialHandoverProps {
  /** What kind of secret this is, which decides the copy around it. */
  kind: "temporary-password" | "recovery-link";
  secret: string;
  /** The account this belongs to, for the operator to check they have the right one. */
  accountLabel: string;
  /**
   * Whether the platform will COMPEL a password change at first sign-in.
   * Comes from the server (passwordChangeEnforced) rather than being assumed,
   * so this panel stops asking operators to instruct people the day
   * auth-service can enforce it itself.
   */
  passwordChangeEnforced: boolean;
  /** Shown for a newly created account, which cannot be used until verified. */
  pendingEmailVerification?: boolean;
  onDismiss: () => void;
}

type CopyState = "idle" | "copied" | "failed";

export function CredentialHandover({
  kind,
  secret,
  accountLabel,
  passwordChangeEnforced,
  pendingEmailVerification = false,
  onDismiss,
}: CredentialHandoverProps) {
  const [copyState, setCopyState] = useState<CopyState>("idle");

  const isPassword = kind === "temporary-password";
  const secretLabel = isPassword ? "Temporary password" : "One-time recovery link";
  // The one line this panel keeps once the four-paragraph version is gone:
  // when it is safe to hand over, and what happens next. Never the "hold it
  // until you have issued the grant" line that used to sit here — the wizard
  // issues the grant itself, in the same run, so there is no separate
  // operator decision to wait on.
  const shareLine = isPassword
    ? [
        `Share it securely${pendingEmailVerification ? " once they verify their email" : ""}.`,
        passwordChangeEnforced
          ? "They must set a new password at first sign-in."
          : "They should change it after first sign-in.",
      ].join(" ")
    : "Expires in 24 hours and works once. Their sessions are already signed out. Share it securely.";

  async function handleCopy() {
    try {
      // Optional chaining rather than a bare call: navigator.clipboard is
      // undefined outside a secure context (and in jsdom), and reading .writeText
      // off undefined throws a TypeError that this catch would report as a copy
      // failure — which is the right outcome, but only by accident.
      const write = navigator.clipboard?.writeText;
      if (!write) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(secret);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  return (
    <section className={styles.panel} role="alert" aria-labelledby="credential-handover-title">
      <header className={styles.header}>
        <Warning size={18} weight="fill" className={styles.warningIcon} aria-hidden />
        <div>
          <h3 className={styles.title} id="credential-handover-title">
            Shown once, copy it now
          </h3>
          <p className={styles.subtitle}>Not stored anywhere. If lost, issue a recovery link.</p>
        </div>
      </header>

      <dl className={styles.fields}>
        <div className={styles.field}>
          <dt className={styles.fieldLabel}>Account</dt>
          <dd className={styles.fieldValue}>{accountLabel}</dd>
        </div>
        <div className={styles.field}>
          <dt className={styles.fieldLabel}>{secretLabel}</dt>
          <dd className={styles.secretRow}>
            <code className={styles.secret} data-testid="handover-secret">
              {secret}
            </code>
            <button
              type="button"
              className={styles.copyButton}
              onClick={() => void handleCopy()}
              aria-label={`Copy ${secretLabel.toLowerCase()}`}
            >
              {copyState === "copied" ? <Check size={14} weight="bold" /> : <Copy size={14} />}
              <span>{copyState === "copied" ? "Copied" : "Copy"}</span>
            </button>
          </dd>
        </div>
      </dl>

      {copyState === "failed" ? (
        <p className={styles.copyFailed}>
          Couldn&apos;t copy automatically. Select the {isPassword ? "password" : "link"} above and
          copy it manually.
        </p>
      ) : null}

      <p className={styles.shareLine}>{shareLine}</p>

      <button type="button" className={styles.dismissButton} onClick={onDismiss}>
        Dismiss
      </button>
    </section>
  );
}
